<template>
  <div class="avis-of-overlay" @click.self="emit('close')">
    <div class="avis-of-modal">
      <div class="avis-of-header">
        <h3>Avis de l'organisme de formation</h3>
        <button class="avis-close-btn" type="button" @click="emit('close')">&times;</button>
      </div>

      <div class="avis-of-body">
        <div class="avis-two-col">

          <!-- Colonne gauche : radio + contact -->
          <div class="avis-col-left">
            <!-- Validation de l'EMT -->
            <div class="avis-field">
              <p class="avis-label">Validation de l'EMT</p>
              <div class="avis-radio-group avis-radio-group--inline">
                <label
                    v-for="option in EMT_OPTIONS"
                    :key="option.value"
                    class="avis-radio-card avis-radio-card--small"
                    :class="[option.colorClass, { 'is-selected': situationEmt === option.value }]"
                >
                  <input v-model="situationEmt" :value="option.value" type="radio" class="avis-radio-input" />
                  <span class="avis-radio-check"></span>
                  <span class="avis-radio-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <div class="avis-field">
              <p class="avis-label">Appréciations</p>
              <div class="avis-radio-group">
                <label
                    v-for="option in AVIS_OPTIONS"
                    :key="option.value"
                    class="avis-radio-card"
                    :class="[option.colorClass, { 'is-selected': avis === option.value }]"
                >
                  <input v-model="avis" :value="option.value" type="radio" class="avis-radio-input" />
                  <span class="avis-radio-check"></span>
                  <span class="avis-radio-text">{{ option.label }}</span>
                </label>
              </div>
            </div>

            <!-- Commentaire employeur -->
            <div class="avis-field">
              <div class="avis-label-row">
                <label class="avis-label" for="avis-comment-employer">Commentaire communiqué à l'employeur</label>
                <button v-if="canResetEmployer" class="reset-btn" type="button" @click="resetEmployerComment">
                  ↺ Réinitialiser
                </button>
              </div>

              <div v-if="avis === 'reserves'" class="reserves-contact-row">
                <span class="reserves-contact-prefix">Pour plus d'information, veuillez contacter :</span>
                <div v-if="!showContactSearch" class="contact-name-row">
                  <button
                      class="contact-name-btn"
                      :class="{ 'is-placeholder': !selectedInterviewerName }"
                      type="button"
                      @click="showContactSearch = true"
                  >
                    {{ selectedInterviewerName || 'Prénom Nom (à compléter)' }}
                    <svg viewBox="0 0 14 14" fill="none" class="contact-edit-icon"><path d="M9.5 2l2.5 2.5-7 7H2.5v-2.5l7-7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <button v-if="selectedInterviewerName" class="contact-name-clear" type="button" @click="clearInterviewerSelection">&times;</button>
                </div>
                <div v-else class="contact-search-wrap">
                    <SearchInput
                        v-model="interviewerSearchTerm"
                        :clear-button-title="UI_MESSAGES.CLEAR_BUTTON_TITLE"
                        :has-value="!!selectedInterviewerObjId"
                        :loading="interviewerSearchLoading"
                        :label="''"
                        :placeholder="selectedInterviewerObjId ? UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER_MODIFY : UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER"
                        :suggestion-key-field="'interviewManagerObjId'"
                        :suggestions="interviewerSuggestions"
                        @clear="clearInterviewerSelection"
                        @search="onInterviewerSearch"
                        @select="selectInterviewer"
                        @clear-suggestions="clearInterviewerSuggestions"
                    >
                      <template #suggestion="{ suggestion }">
                        {{ formatPersonDisplayName(suggestion) }}
                        <small v-if="suggestion.email || suggestion.phone"> — {{ [suggestion.email, suggestion.phone].filter(Boolean).join(', ') }}</small>
                      </template>
                    </SearchInput>
                    <button class="contact-search-cancel" type="button" @click="showContactSearch = false">Annuler</button>
                </div>
              </div>

              <textarea
                  id="avis-comment-employer"
                  v-model="commentEmployeur"
                  class="avis-textarea"
                  rows="4"
                  placeholder="Saisissez un commentaire à destination de l'employeur..."
                  @input="employerEdited = true"
              />
            </div>
          </div>

          <!-- Colonne droite : commentaire candidat pleine hauteur -->
          <div class="avis-col-right">
            <div class="avis-field avis-field--grow">
              <div class="avis-label-row">
                <label class="avis-label" for="avis-comment-candidate">Commentaire communiqué au candidat</label>
                <button v-if="canResetCandidat" class="reset-btn" type="button" @click="resetCandidatComment">
                  ↺ Réinitialiser
                </button>
              </div>

              <div class="candidate-comment-wrap candidate-comment-wrap--grow">
                <div
                    v-if="showPhraseComposer"
                    class="phrase-trigger-zone"
                >
                  <button class="phrase-trigger-btn" type="button" @click="showPhrases = !showPhrases">
                    <svg viewBox="0 0 16 16" fill="none" class="phrase-trigger-icon"><path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    Suggestions de phrases
                    <span v-if="selectedPhrases.length > 0" class="phrase-count">{{ selectedPhrases.length }}</span>
                  </button>
                </div>

                <textarea
                    id="avis-comment-candidate"
                    v-model="commentCandidat"
                    class="avis-textarea avis-textarea--grow"
                    placeholder="Saisissez un commentaire à destination du candidat..."
                    @input="candidatEdited = true"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Dropdown global — flotte sur toute la zone body -->
        <div
            v-if="showPhraseComposer"
            v-show="showPhrases"
            class="phrase-dropdown"
        >
          <button class="phrase-dropdown-close" type="button" @click="showPhrases = false">&times;</button>
          <p class="phrase-hint">Cliquez pour inclure ou retirer une phrase :</p>
          <div
              v-for="phrase in activePhrases"
              :key="phrase.id"
              class="phrase-card"
              :class="{ 'is-active': selectedPhrases.includes(phrase.id) }"
              @click="togglePhrase(phrase)"
          >
            <span class="phrase-card-toggle">
              <svg v-if="selectedPhrases.includes(phrase.id)" viewBox="0 0 20 20" fill="none" class="phrase-check-svg">
                <circle cx="10" cy="10" r="9" fill="#38a3a5"/>
                <path d="M6 10.5l2.5 2.5 5.5-5.5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span v-else class="phrase-add-circle">+</span>
            </span>
            <span class="phrase-card-text">{{ phrase.text }}</span>
          </div>
        </div>
      </div>

      <div class="avis-of-footer">
        <p v-if="sendError" class="avis-send-error">{{ sendError }}</p>
        <button class="avis-cancel-btn" type="button" @click="emit('close')">Annuler</button>
        <button class="avis-send-btn" type="button" :disabled="isSubmitting" @click="handleSend">{{ isSubmitting ? 'Envoi...' : 'Envoyer' }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue'
import SearchInput from '@/components/UI/SearchInput.vue'
import { formatPersonDisplayName, useSearchAutocomplete } from '@/composables/useSearchAutocomplete'
import { usePlanningStore } from '@/store/planningStore'
import { SEARCH_CONFIG, UI_MESSAGES } from '@/constants/planning'
import PlanningApi from '@/api/PlanningApi'

const EMT_OPTIONS = [
  { value: 'oui',      label: 'Oui',      colorClass: 'color-green'  },
  { value: 'non',      label: 'Non',      colorClass: 'color-red'    },
  { value: 'en_cours', label: 'En cours', colorClass: 'color-orange' },
]

const AVIS_OPTIONS = [
  { value: 'tres_favorable', label: 'Avis très favorable', colorClass: 'color-green' },
  { value: 'favorable',      label: 'Avis favorable',      colorClass: 'color-blue'  },
  { value: 'reserves',       label: 'Réserves',             colorClass: 'color-orange'},
]

const PHRASES_FAVORABLE = [
  { id: 'base', text: 'Projet professionnel réaliste et mature.' },
  { id: 'org',  text: 'Votre capacité à suivre la formation pourra nécessiter un soutien dans votre organisation du travail.' },
  { id: 'info', text: 'Votre capacité à suivre la formation pourra nécessiter un soutien dans votre gestion des informations.' },
]

const PHRASES_RESERVES = [
  { id: 'asv',     text: "Nous vous conseillons d'améliorer votre information et vos pratiques sur les connaissances et exigences du métier d'ASV." },
  { id: 'equipe',  text: "Nous vous conseillons d'améliorer votre positionnement et vos responsabilités dans une équipe soignante." },
  { id: 'animaux', text: "Nous vous conseillons d'améliorer votre approche des catégories d'animaux pour lesquelles des difficultés ont été identifiées lors de l'entretien." },
  { id: 'client',  text: "Nous vous conseillons d'améliorer votre aisance dans le contact client et la relation de service." },
  { id: 'org',     text: 'Votre capacité à suivre la formation pourra nécessiter un soutien dans votre organisation du travail.' },
  { id: 'info',    text: 'Votre capacité à suivre la formation pourra nécessiter un soutien dans votre gestion des informations.' },
]

const props = defineProps({
  candidateUrl:      { type: String,           default: null },
  ofObjId:           { type: [String, Number], required: true },
  userAccountObjId:  { type: [String, Number], required: true },
  meetingId:         { type: [String, Number], required: true },
})

const emit = defineEmits(['close', 'send'])

const planningStore = usePlanningStore()

const avis = ref(null)
const situationEmt = ref(null)
const commentEmployeur = ref('')
const commentCandidat = ref('')
const selectedInterviewerObjId = ref(null)
const selectedInterviewerName = ref('')
const selectedInterviewerEmail = ref('')
const selectedInterviewerPhone = ref('')
const showContactSearch = ref(false)
const selectedPhrases = reactive([])
const showPhrases = ref(false)
const employerEdited = ref(false)
const candidatEdited = ref(false)

// ── Autocomplete ─────────────────────────────────────────────────────────────
const searchFunction = (term) =>
  planningStore.searchInterviewManagers(props.ofObjId, props.userAccountObjId, term)

const {
  searchTerm: interviewerSearchTerm,
  suggestions: interviewerSuggestions,
  searchLoading: interviewerSearchLoading,
  onSearch,
  clearSuggestions: clearInterviewerSuggestions,
  clearSearch: clearInterviewerSearch,
  selectSuggestion
} = useSearchAutocomplete(searchFunction, SEARCH_CONFIG.DEBOUNCE_MS, SEARCH_CONFIG.MIN_LENGTH)

const onInterviewerSearch = (value) => {
  interviewerSearchTerm.value = value
  onSearch()
}
const selectInterviewer = (manager) => {
  const selected = selectSuggestion(manager)
  selectedInterviewerObjId.value = selected.interviewManagerObjId || selected.id || null
  selectedInterviewerName.value = formatPersonDisplayName(selected)
  selectedInterviewerEmail.value = selected.email || ''
  selectedInterviewerPhone.value = selected.phone || ''
  showContactSearch.value = false
}
const clearInterviewerSelection = () => {
  selectedInterviewerObjId.value = null
  selectedInterviewerName.value = ''
  selectedInterviewerEmail.value = ''
  selectedInterviewerPhone.value = ''
  clearInterviewerSearch()
  showContactSearch.value = false
}

// ── Auto-fill logic ───────────────────────────────────────────────────────────
const autoEmployerText = computed(() => {
  if (avis.value === 'tres_favorable') return 'Projet professionnel réaliste et mature. Forte motivation et engagement du candidat.'
  if (avis.value === 'favorable')      return 'Projet professionnel réaliste et mature.'
  if (avis.value === 'reserves') {
    const name = selectedInterviewerName.value?.trim() || 'Prénom Nom (à compléter)'
    const lines = [`Pour plus d'information, veuillez contacter :`]
    lines.push(`${name}`)
    if (selectedInterviewerEmail.value) lines.push(`email : ${selectedInterviewerEmail.value}`)
    if (selectedInterviewerPhone.value) lines.push(`téléphone : ${selectedInterviewerPhone.value}`)
    return lines.join('\n')
  }
  return ''
})

watch(autoEmployerText, (newText) => {
  if (!employerEdited.value) {
    commentEmployeur.value = newText
  }
})

const showPhraseComposer = computed(() =>
  avis.value === 'favorable' || avis.value === 'reserves'
)

const activePhrases = computed(() =>
  avis.value === 'reserves' ? PHRASES_RESERVES : PHRASES_FAVORABLE
)

const canResetEmployer = computed(() =>
  avis.value && employerEdited.value && autoEmployerText.value
)

const canResetCandidat = computed(() =>
  avis.value === 'tres_favorable' && candidatEdited.value
)

const resetEmployerComment = () => {
  commentEmployeur.value = autoEmployerText.value
  employerEdited.value = false
}

const resetCandidatComment = () => {
  commentCandidat.value = 'Projet professionnel réaliste et mature. Forte motivation et engagement du candidat.'
  candidatEdited.value = false
}

const togglePhrase = (phrase) => {
  const idx = selectedPhrases.indexOf(phrase.id)
  if (idx !== -1) selectedPhrases.splice(idx, 1)
  else selectedPhrases.push(phrase.id)
  candidatEdited.value = false
  commentCandidat.value = activePhrases.value
    .filter(p => selectedPhrases.includes(p.id))
    .map(p => p.text)
    .join('\n\n')
}

// ── Watchers ──────────────────────────────────────────────────────────────────
watch(avis, () => {
  employerEdited.value = false
  candidatEdited.value = false
  showPhrases.value = false
  showContactSearch.value = false
  selectedInterviewerName.value = ''
  selectedInterviewerEmail.value = ''
  selectedInterviewerPhone.value = ''
  selectedInterviewerObjId.value = null
  clearInterviewerSearch()
  selectedPhrases.splice(0, selectedPhrases.length)
  commentEmployeur.value = autoEmployerText.value
  commentCandidat.value = avis.value === 'tres_favorable'
    ? 'Projet professionnel réaliste et mature. Forte motivation et engagement du candidat.'
    : ''
})

// ── Load existing data ────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const { data } = await PlanningApi.getAvisOf(props.ofObjId, props.userAccountObjId, props.meetingId)
    if (!data) return

    const avisLower = data.avis?.toLowerCase()
    const avisOption = AVIS_OPTIONS.find(o => o.label.toLowerCase() === avisLower || o.value.toLowerCase() === avisLower)
    if (avisOption) avis.value = avisOption.value

    const emtLower = data.situationEmt?.toLowerCase()
    const emtOption = EMT_OPTIONS.find(o => o.label.toLowerCase() === emtLower || o.value.toLowerCase() === emtLower)
    if (emtOption) situationEmt.value = emtOption.value

    await nextTick()

    if (data.commentEmployeur != null) {
      employerEdited.value = true
      commentEmployeur.value = data.commentEmployeur
    }
    if (data.commentCandidat != null) {
      candidatEdited.value = true
      commentCandidat.value = data.commentCandidat
    }
  } catch {
    // pas de données existantes, on laisse le formulaire vide
  }
})

// ── Send ──────────────────────────────────────────────────────────────────────
const isSubmitting = ref(false)
const sendError = ref(null)

const validationError = computed(() => {
  const missing = []
  if (!situationEmt.value) missing.push('Validation de l\'EMT')
  if (!avis.value) missing.push('Appréciations')
  if (!commentEmployeur.value?.trim()) missing.push('Commentaire employeur')
  if (!commentCandidat.value?.trim()) missing.push('Commentaire candidat')
  return missing.length ? `Champs obligatoires manquants : ${missing.join(', ')}.` : null

})

const handleSend = async () => {
  if (isSubmitting.value) return
  if (validationError.value) {
    sendError.value = validationError.value
    return
  }
  isSubmitting.value = true
  sendError.value = null
  try {
    const avisLabel = AVIS_OPTIONS.find(o => o.value === avis.value)?.label ?? avis.value
    await PlanningApi.submitAvisOf(props.ofObjId, props.userAccountObjId, props.meetingId, {
      avis: avisLabel,
      commentEmployeur: commentEmployeur.value,
      commentCandidat: commentCandidat.value,
      situationEmt: EMT_OPTIONS.find(o => o.value === situationEmt.value)?.label ?? situationEmt.value,
    })
    emit('send', {
      avis: avis.value,
      interviewerObjId: selectedInterviewerObjId.value,
      interviewerName: selectedInterviewerName.value,
      commentEmployeur: commentEmployeur.value,
      commentCandidat: commentCandidat.value,
    })
    if (props.candidateUrl) {
      window.open(props.candidateUrl, '_blank', 'noopener,noreferrer')
    }
    emit('close')
  } catch (err) {
    sendError.value = err?.response?.data?.message || err?.message || 'Une erreur est survenue.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.avis-of-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(9, 18, 32, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1700;
  padding: 12px;
}

.avis-of-modal {
  width: 820px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.25);
}

/* Header */
.avis-of-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--primary, #1f5f97);
}

.avis-of-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #ffffff;
}

.avis-close-btn {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  border-radius: 6px;
  transition: background 0.15s;
  display: flex;
  align-items: center;
}

.avis-close-btn:hover,
.avis-close-btn:focus {
  background: rgba(255, 255, 255, 0.18);
  outline: none;
}

/* Body */
.avis-of-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f6f8fb;
  position: relative;
  display: flex;
  flex-direction: column;
}

.avis-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: stretch;
  flex: 1;
}

.avis-col-left,
.avis-col-right {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.avis-field--grow {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.candidate-comment-wrap--grow {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.avis-textarea--grow {
  flex: 1;
  resize: none;
  min-height: 120px;
}

.avis-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Label row with optional reset */
.avis-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.avis-label {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  color: #2d3748;
}

.reset-btn {
  flex-shrink: 0;
  border: none;
  background: none;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}

.reset-btn:hover,
.reset-btn:focus {
  color: #1f5f97;
  background: #e8f0fb;
  outline: none;
}

/* Radio cards */
.avis-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.avis-radio-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 2px solid transparent;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07);
  user-select: none;
}

.avis-radio-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.avis-radio-check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.18s, background 0.18s;
}

.avis-radio-check::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.18s;
}

.avis-radio-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
}

.color-green .avis-radio-check  { border-color: #6ee7b7; }
.color-blue  .avis-radio-check  { border-color: #93c5fd; }
.color-orange .avis-radio-check { border-color: #fcd34d; }

.color-green.is-selected  { border-color: #10b981; background: #ecfdf5; box-shadow: 0 0 0 3px rgba(16,185,129,.12); }
.color-blue.is-selected   { border-color: #3b82f6; background: #eff6ff; box-shadow: 0 0 0 3px rgba(59,130,246,.12); }
.color-orange.is-selected { border-color: #f59e0b; background: #fffbeb; box-shadow: 0 0 0 3px rgba(245,158,11,.12); }

.color-green.is-selected  .avis-radio-check { border-color: #10b981; background: #10b981; }
.color-blue.is-selected   .avis-radio-check { border-color: #3b82f6; background: #3b82f6; }
.color-orange.is-selected .avis-radio-check { border-color: #f59e0b; background: #f59e0b; }

.color-green.is-selected  .avis-radio-check::after,
.color-blue.is-selected   .avis-radio-check::after,
.color-orange.is-selected .avis-radio-check::after { background: #ffffff; }

.color-green.is-selected  .avis-radio-text { color: #065f46; font-weight: 600; }
.color-blue.is-selected   .avis-radio-text { color: #1e3a8a; font-weight: 600; }
.color-orange.is-selected .avis-radio-text { color: #78350f; font-weight: 600; }

.avis-radio-card:hover:not(.is-selected) { background: #f0f4f8; border-color: #d8e0ea; }

.avis-radio-group--inline {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
}

.avis-radio-card--small {
  padding: 7px 11px;
  font-size: 0.85rem;
}

.color-red .avis-radio-check  { border-color: #fca5a5; }
.color-red.is-selected { border-color: #ef4444; background: #fef2f2; box-shadow: 0 0 0 3px rgba(239,68,68,.12); }
.color-red.is-selected .avis-radio-check { border-color: #ef4444; background: #ef4444; }
.color-red.is-selected .avis-radio-check::after { background: #ffffff; }
.color-red.is-selected .avis-radio-text { color: #7f1d1d; font-weight: 600; }

/* Phrase composer */
.candidate-comment-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phrase-trigger-zone {
  position: relative;
}

.phrase-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 12px;
  cursor: default;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  user-select: none;
}

.phrase-trigger-zone:hover .phrase-trigger-btn {
  border-color: #38a3a5;
  background: #f0fdfa;
  color: #0d9488;
}

.phrase-trigger-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.phrase-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #38a3a5;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  width: 17px;
  height: 17px;
  border-radius: 50%;
}

.phrase-dropdown {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 50%;
  z-index: 100;
  background: #ffffff;
  border-left: 1.5px solid #e2e8f0;
  border-radius: 0 0 12px 0;
  padding: 16px;
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.phrase-dropdown-close {
  position: absolute;
  top: 8px;
  right: 10px;
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 2px 5px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}

.phrase-dropdown-close:hover,
.phrase-dropdown-close:focus {
  color: #ef4444;
  background: #fee2e2;
  outline: none;
}

.phrase-hint {
  margin: 0 0 4px;
  font-size: 0.78rem;
  color: #64748b;
  font-style: italic;
  padding-right: 24px;
}

.phrase-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 12px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
  user-select: none;
}

.phrase-card:hover:not(.is-active) {
  border-color: #94a3b8;
  background: #f8fafc;
}

.phrase-card.is-active {
  border-color: #38a3a5;
  background: #f0fdfa;
  box-shadow: 0 0 0 3px rgba(56, 163, 165, 0.1);
}

.phrase-card-toggle {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.phrase-check-svg {
  width: 20px;
  height: 20px;
}

.phrase-add-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  line-height: 1;
  transition: border-color 0.18s, color 0.18s;
}

.phrase-card:hover:not(.is-active) .phrase-add-circle {
  border-color: #64748b;
  color: #64748b;
}

.phrase-card-text {
  font-size: 0.87rem;
  line-height: 1.5;
  color: #374151;
}

.phrase-card.is-active .phrase-card-text {
  color: #134e4a;
  font-weight: 500;
}

/* Reserves: inline contact selector */
.reserves-contact-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 0.85rem;
}

.reserves-contact-prefix {
  color: #78350f;
  font-weight: 500;
  white-space: nowrap;
}

.contact-name-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1.5px dashed #f59e0b;
  border-radius: 5px;
  background: #ffffff;
  color: #92400e;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 3px 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.contact-name-btn:hover,
.contact-name-btn:focus {
  border-color: #d97706;
  background: #fef3c7;
  color: #78350f;
  outline: none;
}

.contact-name-btn.is-placeholder {
  color: #b45309;
  font-style: italic;
}

.contact-edit-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  opacity: 0.7;
}

.contact-name-clear {
  border: none;
  background: none;
  color: #9ca3af;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}

.contact-name-clear:hover,
.contact-name-clear:focus {
  color: #ef4444;
  background: #fee2e2;
  outline: none;
}

.contact-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.contact-search-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.contact-search-cancel {
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 5px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
}

.contact-search-cancel:hover,
.contact-search-cancel:focus {
  background: #f3f4f6;
  border-color: #9ca3af;
  outline: none;
}

/* Textareas */
.avis-textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: #1f2937;
  background: #fff;
  outline: none;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  box-sizing: border-box;
}

.avis-textarea:focus {
  border-color: #38a3a5;
  box-shadow: 0 0 0 2px rgba(56, 163, 165, 0.16);
}

/* Footer */
.avis-of-footer {
  flex-shrink: 0;
  border-top: 1px solid #d8e0ea;
  background: #ffffff;
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.avis-cancel-btn {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.avis-cancel-btn:hover,
.avis-cancel-btn:focus {
  background: #f3f4f6;
  border-color: #9ca3af;
  outline: none;
}

.avis-send-btn {
  border: none;
  border-radius: 8px;
  background: #1f5f97;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 10px 20px;
  cursor: pointer;
  transition: background 0.2s;
}

.avis-send-btn:hover:not(:disabled),
.avis-send-btn:focus:not(:disabled) {
  background: #174c79;
  outline: none;
}

.avis-send-error {
  flex: 1;
  margin: 0;
  font-size: 0.85rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  padding: 6px 10px;
}

.avis-send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
